import React from 'react';
import { Form, useChanges } from '../../../../components/Form/Form';
import { Select } from '../../../../components/Form/Select/Select';
import { TextField } from '../../../../components/Form/TextField/TextField';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { handleError } from '../../../../model/notification';
import { t } from '../../../../locales/translate';
import { addPayment, PaymentModel, paymentMethods, updateBankPayment } from '../../../../model/payment';
import { Checkbox } from '../../../../components/Form/Checkbox/Checkbox';
import { Label } from '../../../../components/Form/Label/Label';
import { Stack } from '../../../../components/Stack/Stack';
import { Spacer } from '../../../../components/Spacer/Spacer';
import { Grid } from '../../../../components/Grid/Grid';
import { FormNumberField } from '../../../../components/Form/NumberField/FormNumberField';

interface Props {
  mode?: 'edit' | 'create';
  controller: ModalController<PaymentModel>;
  orderId?: string;
  paymentId?: string;
}

export function PaymentModal({ mode = 'create', controller, orderId, paymentId }: Props): JSX.Element {
  const createMode = mode === 'create';
  const [, setChanges, model] = useChanges({
    ...controller.model,
    provider: controller.model.provider || 'bank_transfer',
    amount: controller.model.amount,
  });

  async function handleOnSubmit(): Promise<void> {
    try {
      if (mode && orderId) {
        if (createMode) {
          await addPayment(orderId, {
            ...model,
          });
        } else {
          if (paymentId) {
            await updateBankPayment(orderId, paymentId, { ...model });
          }
        }
      }
      controller.close();
    } catch (e) {
      handleError(e);
    }
  }

  return (
    <Form className="add-payment-modal" model={model} onInput={setChanges}>
      {(form) => {
        controller.configure({
          onSubmit: handleOnSubmit,
          canSubmit: form.valid,
        });
        return (
          <Stack>
            <Select
              required
              name="provider"
              label={t.model.payment.paymentMethod}
              options={paymentMethods.map((e) => ({ value: e.id, label: e.name }))}
              disabled={!createMode}
            />
            <FormNumberField
              required
              label={t.model.bank.amount}
              step={0.01}
              disableBlurReset
              value={controller.model.amount}
              onChange={(val) => setChanges({ amount: val || 0 })}
            />
            <Select
              name="status"
              required
              label={t.model.payment.status}
              options={[
                { value: 'pending', label: t.model.status.pending },
                { value: 'paid', label: t.model.status.paid },
              ]}
              disabled={!createMode}
            />
            <Spacer size="none" />
            <Checkbox
              name="skipEmailDelivery"
              label={t.model.payment.skipCustomerEmail}
              readOnly={!createMode}
            ></Checkbox>
            {model.provider === 'bank_transfer' && (
              <>
                <Spacer size="none" />
                <Label title={t.model.bank.bankDetails} />
                <TextField name="bankAccount.name" label={t.model.bank.recipient} />
                <Grid>
                  <TextField name="bankAccount.iban" label={t.model.bank.iban} />
                  <TextField name="bankAccount.bic" label={t.model.bank.bic} />
                </Grid>
                <TextField name="bankAccount.bankName" label={t.model.bank.bankName} />
              </>
            )}
          </Stack>
        );
      }}
    </Form>
  );
}
